import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Placeholder data for instruments - you might want to fetch this from an API later
const instruments = [
  'Accordion', 'Bagpipes', 'Banjo', 'Baroque bassoon', 'Baroque flute',
  'Baroque oboe', 'Bass', 'Bass Guitar', 'Bassoon', 'Cello', 'Clarinet',
  'Drums', 'Flute', 'French horn', 'Guitar', 'Harmonica', 'Harp', 'Keyboard',
  'Mandolin', 'Oboe', 'Piano', 'Piccolo', 'Recorder', 'Saxophone',
  'Sitar', 'Synthesizer', 'Trombone', 'Trumpet', 'Tuba', 'Ukulele',
  'Viola', 'Violin', 'Voice / Singing', 'Xylophone'
];

const InstrumentSelectionPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInstruments, setFilteredInstruments] = useState(instruments);
  const [selectedInstrument, setSelectedInstrument] = useState<string | null>(null);
  const [step, setStep] = useState(1); // 1 for instrument, 2 for learner type
  const navigate = useNavigate();

  useEffect(() => {
    console.log('InstrumentSelectionPage rendered, step:', step, 'selectedInstrument:', selectedInstrument);
  }, [step, selectedInstrument]);

  useEffect(() => {
    setFilteredInstruments(
      instruments.filter(instrument =>
        instrument.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm]);

  const handleInstrumentSelect = (instrument: string) => {
    setSelectedInstrument(instrument);
    console.log(`Instrument selected: ${instrument}`);
    setStep(2);
  };

  const handleLearnerTypeSelect = (learnerType: 'learner' | 'guardian') => {
    console.log(`Learner type selected: ${learnerType}, for instrument: ${selectedInstrument}`);
    const instrumentQueryParam = encodeURIComponent(selectedInstrument || 'music');
    navigate(`/new-student/questions?instrument=${instrumentQueryParam}&learnerType=${learnerType}`);
  };

  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 text-gray-900 flex flex-col items-center justify-center p-4 pt-20 sm:pt-24">
        <div className="w-full max-w-lg bg-white p-8 sm:p-10 rounded-xl shadow-2xl">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-center text-gray-900 mb-8">
            Select an instrument to get started!
          </h1>
          <input
            type="text"
            placeholder="Search for an instrument (e.g., Piano, Guitar)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-5 py-3.5 mb-6 bg-gray-50 text-gray-800 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder-gray-500 text-base"
          />
          <div className="max-h-72 sm:max-h-80 overflow-y-auto space-y-2.5 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {filteredInstruments.length > 0 ? (
              filteredInstruments.map(instrument => (
                <button
                  key={instrument}
                  onClick={() => handleInstrumentSelect(instrument)}
                  className="w-full text-left px-5 py-3.5 bg-gray-100 hover:bg-blue-100 text-gray-800 hover:text-blue-700 font-medium rounded-lg transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {instrument}
                </button>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No instruments match your search.</p>
            )}
          </div>
          <div className="mt-10 text-center">
            <button 
              onClick={() => navigate('/become-a-teacher')} 
              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              Join as a teacher
            </button>
          </div>
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              Already have an account? <button onClick={() => navigate('/login')} className="font-medium text-blue-600 hover:text-blue-700 hover:underline">Sign In</button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Learner Type Selection
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 text-gray-900 flex flex-col items-center justify-center p-4 pt-20">
        <div className="w-full max-w-lg text-center bg-white p-8 sm:p-10 rounded-xl shadow-2xl">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              One last step!
            </h1>
            <p className="text-lg text-gray-600 mb-10 max-w-md mx-auto">
                Are you the one learning {selectedInstrument ? selectedInstrument.toLowerCase() : 'the instrument'}, or are you signing up for someone else?
            </p>
            
            <button 
                onClick={() => handleLearnerTypeSelect('learner')}
                className="w-full sm:w-auto sm:min-w-[280px] mx-auto text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-semibold rounded-full text-lg px-8 py-4 text-center mb-4 transition-colors duration-150 ease-in-out transform hover:-translate-y-0.5 shadow-md hover:shadow-lg"
            >
                I'm the learner
            </button>
            <button 
                onClick={() => handleLearnerTypeSelect('guardian')}
                className="w-full sm:w-auto sm:min-w-[280px] mx-auto text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-semibold rounded-full text-lg px-8 py-4 text-center transition-colors duration-150 ease-in-out transform hover:-translate-y-0.5 shadow-md hover:shadow-lg"
            >
                I'm the learner's parent or guardian
            </button>
        </div>
    </div>
  );
};

export default InstrumentSelectionPage; 